let devices = undefined;
let selectedDevice;

export const needUserSelectPrinter = async (label) => {
    try {
        // Refresh the list of devices.
        if (!devices) 
            await loadDevices();
        if (devices.length < 1) 
            return false; // this should cause an error message.

        // See if the desired label width has a configuration available
        let labelWidthConfig = getLabelWidthConfig();
        let width = label['label-width'];
        for (let i = 0; i < devices.length; i++) {
            let deviceUUID = devices[i]['uid'];
            if (deviceUUID in labelWidthConfig['printers']) {
                if (labelWidthConfig['printers'][deviceUUID] === width) {
                    return false;
                }
            }
        }
        return true;
    }
    catch (error) {
        // no printers to ask the user to select from.
        console.log("BrowserPrint Error: " + error.message);
        return false;
    }
}

export const getLabelPrinterDevices = () => {
    return devices;
}

// The "printer uid -> label width" mapping the browser remembers.
// Exposed read-only so the printer reset page can show what is configured.
export const getPrinterWidthConfig = () => {
    try {
        return getLabelWidthConfig()['printers'];
    }
    catch (error) {
        console.log("BrowserPrint Error: " + error.message);
        return {};
    }
}

// Discovery is normally done once per page load and kept in the `devices`
// cache, so a printer plugged in afterwards would never be seen. This forces
// a new discovery.
export const refreshLabelPrinterDevices = async () => {
    devices = undefined;
    selectedDevice = undefined;
    await loadDevices();
    return devices;
}

// Forgets everything the browser knows about label printers: the discovered
// device list and the "printer uid -> label width" mapping saved in
// localStorage. Used when a printer is replaced: the mapping of the old
// printer would otherwise keep winning and the labels would be sent to it.
// After a reset the next print asks the user which printer holds which format.
export const resetPrinterConfiguration = async () => {
    setLabelWidthConfig(null);
    return await refreshLabelPrinterDevices();
}

export const setPrinterWidthConfig = (labelWidth, deviceUUID) => {
    try {
        let labelWidthConfig = getLabelWidthConfig();

        if (!deviceUUID) return;

        // Make sure the printer UUID only apears once in the config
        if (deviceUUID in labelWidthConfig['printers']) {
            delete labelWidthConfig['printers'][deviceUUID];
        }
        labelWidthConfig['printers'][deviceUUID] = labelWidth;
        setLabelWidthConfig(labelWidthConfig);
    }
    catch (error) {
        // no printers to ask the user to select from.
        console.log("BrowserPrint Error: " + error.message);
    }
}

export const selectPrinter = (label) => {
    try {
        let width = label['label-width'];
        let labelWidthConfig = getLabelWidthConfig();

        // If there are no devices, exit out without setting a printer
        if (!devices) return false;
        if (devices.length < 1) return false;

        for (let i = 0; i < devices.length; i++) {
            let deviceUUID = devices[i]['uid'];
            if (deviceUUID in labelWidthConfig['printers']) {
                if (labelWidthConfig['printers'][deviceUUID] === width) {
                    selectedDevice = devices[i];
                    return true;
                }
            }
        }
        // Unable to select a device (Shouldn't get here in normal
        // execution.)
        return false;
    }
    catch (error) {
        // no printers to ask the user to select from.
        console.log("BrowserPrint Error: " + error.message);
        return false;
    }
}

// Delay left between two labels so the printer buffer has time to breathe
// when a whole level of bins is sent at once.
const BULK_PRINT_DELAY_MS = 150;

export const printZebraLabels = async (labels) => {
    if (!labels || labels.length < 1) {
        return {"status": 400, "message": "No label to print."};
    }

    for (let i = 0; i < labels.length; i++) {
        let status = printZebraLabel(labels[i]);
        if (status['status'] !== 200) {
            return status;
        }
        if (i < labels.length - 1) {
            await sleep(BULK_PRINT_DELAY_MS);
        }
    }
    return {"status": 200, "message": "OK"};
}

export const printZebraLabel=(label) => {

    console.log(JSON.stringify(label));
    // 1. Get the existing label printer config from the state
    try {
        // Assume that the device is selected.
        // This should happen at a higher level.

        // 1. Wait for the printer to be ready - Timout = error.
        // 2. Send the template to the printer.
        writeToSelectedPrinter(label['template']);
        // 3. Wait for the printer to be ready - Timout = error.
        // 4. Send the data to the printer.
        writeToSelectedPrinter(label['data']);
        // 5. Check if there was an error from the printer
        //    If the printer reports an error, inform the user via error message.
        // 6. Report success.
        return {"status": 200, "message":"OK"};
    }
    catch (error) {
        return {"status": 400, "message": "Label printer error: " + error.message}
    }
}

function getLabelWidthConfig() {
    let ls = window.localStorage;
    let labelWidthConfig = ls.getItem("label-width-config");
    if (!labelWidthConfig) {
        // Deal with no label width config stored
        labelWidthConfig = {};
    } else {
        labelWidthConfig = JSON.parse(labelWidthConfig);
    }
    // The following makes sure the old system gets replaced.
    if (!("printers" in labelWidthConfig)) {
        labelWidthConfig = {"printers": {}};
        setLabelWidthConfig(labelWidthConfig);
    }
    return labelWidthConfig;
}

function setLabelWidthConfig(labelWidthConfig) {
    let ls = window.localStorage;
    // Deal with no label width config stored
    if (!labelWidthConfig) {
        labelWidthConfig = {"printers": {}};
    }
    ls.setItem("label-width-config", JSON.stringify(labelWidthConfig));
}

// BrowserPrint is a callback based SDK talking to a service running on the
// workstation (http://127.0.0.1:9100). That service may not be installed, not
// started, or hold no printer at all, so every outcome has to be handled.
//
// The discovery is wrapped in a promise so its failures travel on the await
// chain: throwing from inside a BrowserPrint callback escapes the try/catch of
// the callers and shows up as an uncaught runtime error in the browser.
function loadDevices() {
    return new Promise((resolve, reject) => {
        if (!window.BrowserPrint) {
            devices = [];
            reject(new Error("Zebra Browser Print is not loaded in this page."));
            return;
        }

        // The SDK reports its failures as the raw XHR response, which is an
        // empty string when the local service cannot be reached. Wrap it so
        // the callers always get a real Error with a usable message.
        const fail = (response) => {
            devices = [];
            reject(new Error("Zebra Browser Print service unreachable" + (response ? ": " + response : ".")));
        };

        window.BrowserPrint.getLocalDevices((deviceList) => {
            devices = (deviceList || []).filter((device) => device.uid);
            resolve(devices);
        }, fail, "printer");
    });
}

function writeToSelectedPrinter(dataToWrite)
{
    if (selectedDevice) {
	    selectedDevice.send(dataToWrite, undefined, errorCallback);
    } else {
        throw(new Error("No label printer detected."));
    }
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// var readCallback = function(readData) {
// 	if(readData === undefined || readData === null || readData === "")
// 	{
// 		alert("No Response from Device");
// 	}
// 	else
// 	{
// 		alert(readData);
// 	}
	
// }

var errorCallback = function(errorMessage){
	console.log("BrowserPrint Error: " + errorMessage);	
}

// function readFromSelectedPrinter()
// {
// 	selectedDevice.read(readCallback, errorCallback);
// }

